/**
 * Tunable caps for the auto-mindmap, kept in one place.
 *
 * ONE budget now: the number of content nodes a map may hold. Wherever children
 * are dropped to stay inside it, the parent gets a single "+N more" stub marking
 * the hidden count.
 *
 * There used to be a depth cap as well, and it outlived the design it belonged
 * to. When generation emitted a whole page at once, refusing to go deeper than
 * four levels was what kept a map readable. Generation is progressive now — root
 * plus level 1, then one level per chip click — so the same cap was cutting off
 * the level the user had just explicitly asked for: expanding a heading builds
 * the tree from the PAGE root, so anything past level 4 came back as a lone
 * "+2 more" with nothing behind it. Depth is the user's business; the node
 * budget is ours.
 */

/**
 * Maximum number of content nodes (excludes "+N more" stubs).
 *
 * This is not about legibility — it is what the editor can carry. Every node is
 * a DOM component in the canvas overlay, ships in every autosave PUT, and is
 * re-broadcast on every collab sync. 150 was chosen when the layout sweep was
 * quadratic in the node count; it is a sweep line now, so the budget can be what
 * the DOM and the payload can stand.
 */
export const MINDMAP_NODE_CAP = 500;

/**
 * How deep the block walk may recurse. A STACK guard, not a product cap: Notion
 * nests far shallower than this, and a synced block that transitively contains
 * itself is caught by the ancestor path — but an unguarded recursion blows the
 * stack with a RangeError, which the fire-and-forget expand call sites swallow.
 */
export const MINDMAP_WALK_DEPTH_LIMIT = 64;

/** Text of the stub node standing in for `count` dropped children. */
export const moreStubText = (count: number): string => `+${count} more`;
